// src/pages/Transport-page/Transport.js
import React, { useState, useEffect, useMemo } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import {
  MdSearch,
  MdEdit,
  MdDelete,
  MdClear,
} from 'react-icons/md';
import {
  FaPlus,
  FaFileExcel,
  FaEye,
  FaTimes,
  FaCheckCircle,
  FaTimesCircle,
  FaTruck,
  FaPhone,
  FaWeightHanging,
} from 'react-icons/fa';
import { HiOutlineViewGrid, HiOutlineViewList } from 'react-icons/hi';
import { BASE_URL } from '../../api';

const API_BASE_URL = `${BASE_URL}/api/master`;

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '40px',
    borderColor: state.isFocused ? '#2563eb' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.2)' : 'none',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    '&:hover': { borderColor: '#2563eb' },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#eff6ff' : 'white',
    color: state.isSelected ? 'white' : '#1f2937',
    fontSize: '0.875rem',
    cursor: 'pointer',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: '0.5rem',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
  }),
};

const initialFormState = {
  vehicleno: '',
  ownername: '',
  vehicletypeid: '',
  vehicleownershipid: '',
  vehiclelength: '',
  vehiclecapacity: '',
  mobileno: '',
  status: 1,
};

const Transport = () => {
  const [transports, setTransports] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [vehicleOwnerships, setVehicleOwnerships] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Search & Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [viewMode, setViewMode] = useState('card');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTransport, setEditingTransport] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [viewItem, setViewItem] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const fetchTransports = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/transport`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch transports');
      const { data } = await res.json();
      setTransports(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || 'Error loading transports');
      setTransports([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicleTypes = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/vehicletype`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch vehicle types');
      const { data } = await res.json();
      setVehicleTypes(Array.isArray(data) ? data : []);
    } catch (err) {
      setVehicleTypes([
        { _id: '1', name: 'LMV' },
        { _id: '2', name: 'HMV' },
      ]);
    }
  };

  const fetchVehicleOwnerships = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/vehicleownership`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch ownerships');
      const { data } = await res.json();
      setVehicleOwnerships(Array.isArray(data) ? data : []);
    } catch (err) {
      setVehicleOwnerships([
        { _id: '1', name: 'Owned' },
        { _id: '2', name: 'Leased' },
      ]);
    }
  };

  useEffect(() => {
    const load = async () => {
      await Promise.all([fetchVehicleTypes(), fetchVehicleOwnerships(), fetchTransports()]);
    };
    if (localStorage.getItem('token')) load();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, typeFilter]);

  // Options for react-select
  const vehicleTypeOptions = useMemo(() => {
    return vehicleTypes.map((t) => ({ value: t._id, label: t.name }));
  }, [vehicleTypes]);

  const vehicleOwnershipOptions = useMemo(() => {
    return vehicleOwnerships.map((o) => ({ value: o._id, label: o.name }));
  }, [vehicleOwnerships]);

  // Validation
  const validate = () => {
    const errors = {};
    if (!form.vehicleno?.trim()) errors.vehicleno = 'Vehicle No is required';
    if (!form.ownername?.trim()) errors.ownername = 'Owner Name is required';
    if (!form.vehicletypeid) errors.vehicletypeid = 'Vehicle Type is required';
    if (!form.vehicleownershipid) errors.vehicleownershipid = 'Ownership is required';

    if (!form.mobileno?.trim()) {
      errors.mobileno = 'Mobile is required';
    } else if (!/^\d{10}$/.test(form.mobileno.trim())) {
      errors.mobileno = 'Must be 10 digits';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOpenModal = (t = null) => {
    setFormErrors({});
    if (t) {
      setEditingTransport(t);
      setForm({
        vehicleno: t.vehicleno || '',
        ownername: t.ownername || '',
        vehicletypeid: t.vehicletypeid?._id || t.vehicletypeid || '',
        vehicleownershipid: t.vehicleownershipid?._id || t.vehicleownershipid || '',
        vehiclelength: t.vehiclelength || '',
        vehiclecapacity: t.vehiclecapacity || '',
        mobileno: t.mobileno || '',
        status: t.status !== 0 ? 1 : 0,
      });
    } else {
      setEditingTransport(null);
      setForm(initialFormState);
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingTransport(null);
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = {
        vehicleno: form.vehicleno.trim().toUpperCase(),
        ownername: form.ownername.trim(),
        vehicletypeid: form.vehicletypeid,
        vehicleownershipid: form.vehicleownershipid,
        vehiclelength: form.vehiclelength ? Number(form.vehiclelength) : 0,
        vehiclecapacity: form.vehiclecapacity ? Number(form.vehiclecapacity) : 0,
        mobileno: form.mobileno.trim(),
        status: form.status,
      };

      const url = editingTransport ? `${API_BASE_URL}/transport/${editingTransport._id}` : `${API_BASE_URL}/transport`;
      const method = editingTransport ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Operation failed');

      toast.success(editingTransport ? 'Vehicle updated successfully!' : 'Vehicle created successfully!');
      handleCloseModal();
      fetchTransports();
    } catch (err) {
      toast.error(err.message || 'Error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, vehicleno) => {
    const result = await Swal.fire({
      title: 'Delete Vehicle?',
      text: `Are you sure you want to delete ${vehicleno || 'this transport record'}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        const res = await fetch(`${API_BASE_URL}/transport/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
        if (!res.ok) throw new Error('Failed to delete transport');
        toast.success('Vehicle record deleted');
        fetchTransports();
      } catch (err) {
        toast.error(err.message || 'Failed to delete');
      }
    }
  };

  // Export Excel
  const handleExportExcel = () => {
    if (transports.length === 0) {
      toast.info('No transport data to export');
      return;
    }
    const exportRows = filteredTransports.map((t, idx) => ({
      'S.No': idx + 1,
      'Vehicle No': t.vehicleno || '-',
      Owner: t.ownername || '-',
      'Vehicle Type': t.vehicletypeid?.name || vehicleTypes.find((v) => v._id === t.vehicletypeid)?.name || '-',
      Ownership: t.vehicleownershipid?.name || vehicleOwnerships.find((o) => o._id === t.vehicleownershipid)?.name || '-',
      'Length (ft)': t.vehiclelength || 0,
      'Capacity (tons)': t.vehiclecapacity || 0,
      Mobile: t.mobileno || '-',
      Status: t.status !== 0 ? 'Active' : 'Inactive',
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Transports');
    XLSX.writeFile(workbook, `Transport_Master_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Vehicles exported to Excel successfully!');
  };

  // Filter Logic
  const filteredTransports = useMemo(() => {
    return transports.filter((t) => {
      const isActive = t.status !== 0;
      if (statusFilter === 'ACTIVE' && !isActive) return false;
      if (statusFilter === 'INACTIVE' && isActive) return false;

      if (typeFilter !== 'ALL') {
        const typeId = t.vehicletypeid?._id || t.vehicletypeid;
        if (typeId !== typeFilter) return false;
      }

      if (!searchTerm.trim()) return true;
      const term = searchTerm.toLowerCase().trim();
      const typeName = t.vehicletypeid?.name || vehicleTypes.find((v) => v._id === t.vehicletypeid)?.name || '';
      const ownName = t.vehicleownershipid?.name || vehicleOwnerships.find((o) => o._id === t.vehicleownershipid)?.name || '';

      return (
        (t.vehicleno || '').toLowerCase().includes(term) ||
        (t.ownername || '').toLowerCase().includes(term) ||
        (t.mobileno || '').includes(term) ||
        typeName.toLowerCase().includes(term) ||
        ownName.toLowerCase().includes(term)
      );
    });
  }, [transports, searchTerm, statusFilter, typeFilter, vehicleTypes, vehicleOwnerships]);

  const totalItems = filteredTransports.length;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredTransports.slice(start, start + pageSize);
  }, [filteredTransports, currentPage, pageSize]);

  const totalActive = useMemo(() => transports.filter((t) => t.status !== 0).length, [transports]);
  const totalInactive = transports.length - totalActive;

  return (
    <div className="p-4 md:p-6 bg-gray-100 min-h-screen">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header & KPI Summary */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 uppercase tracking-wide flex items-center gap-2">
            Transport Master
          </h2>
          <p className="text-gray-500 text-sm mt-0.5">
            Manage cargo fleets, vehicle registrations, payload capacities & driver contacts
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleExportExcel}
            className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2.5 rounded-lg text-sm font-semibold transition shadow-sm"
          >
            <FaFileExcel className="text-green-600 text-base" />
            <span>Export Excel</span>
          </button>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition shadow-md hover:shadow-lg"
          >
            <FaPlus className="text-sm" />
            <span>Add New Transport</span>
          </button>
        </div>
      </div>

      {/* KPI Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Total Fleets</p>
            <p className="text-2xl font-bold text-gray-800">{transports.length}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
            <FaTruck />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Active Fleets</p>
            <p className="text-2xl font-bold text-green-600">{totalActive}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-green-50 text-green-600 flex items-center justify-center font-bold">
            <FaCheckCircle />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Inactive</p>
            <p className="text-2xl font-bold text-gray-500">{totalInactive}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center font-bold">
            <FaTimesCircle />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Types</p>
            <p className="text-2xl font-bold text-indigo-600">{vehicleTypes.length}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold">
            <FaWeightHanging />
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-5 md:p-6 mb-8">
        {/* Toolbar */}
        <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-4 mb-6 pb-5 border-b border-gray-200">
          <div className="relative flex-1 min-w-[260px]">
            <MdSearch className="absolute left-3.5 top-3 text-gray-400 text-xl" />
            <input
              type="text"
              placeholder="Search by vehicle no, owner name, mobile..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="absolute right-3 top-3 text-gray-400">
                <MdClear className="text-lg" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Status Filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-gray-500 uppercase">Status:</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="ALL">All Status</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>

            {/* Type Filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-gray-500 uppercase">Type:</span>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="ALL">All Types</option>
                {vehicleTypes.map((t) => (
                  <option key={t._id} value={t._id}>{t.name}</option>
                ))}
              </select>
            </div>

            {(searchTerm || statusFilter !== 'ALL' || typeFilter !== 'ALL') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('ALL');
                  setTypeFilter('ALL');
                }}
                className="text-xs text-blue-600 hover:text-blue-800 font-semibold px-2 py-1 underline"
              >
                Reset
              </button>
            )}

            {/* View Mode Toggle */}
            <div className="flex items-center bg-gray-100 p-1 rounded-lg border border-gray-200 ml-auto lg:ml-2">
              <button
                type="button"
                onClick={() => setViewMode('card')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition ${
                  viewMode === 'card' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600'
                }`}
              >
                <HiOutlineViewGrid className="text-base" />
                <span className="hidden sm:inline">Cards</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition ${
                  viewMode === 'table' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600'
                }`}
              >
                <HiOutlineViewList className="text-base" />
                <span className="hidden sm:inline">List</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="py-20 text-center text-gray-400 flex flex-col items-center">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-3"></div>
            <p className="text-sm font-semibold">Loading fleet...</p>
          </div>
        ) : paginatedData.length === 0 ? (
          <div className="py-16 text-center text-gray-500 bg-gray-50 rounded-xl border border-dashed border-gray-300">
            <FaTruck className="text-4xl text-gray-300 mx-auto mb-2" />
            <p className="text-base font-bold text-gray-700">No vehicles found</p>
          </div>
        ) : viewMode === 'card' ? (
          /* Landscape Cards Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mb-6">
            {paginatedData.map((t, idx) => {
              const isActive = t.status !== 0;
              const sNo = (currentPage - 1) * pageSize + idx + 1;
              const typeName = t.vehicletypeid?.name || vehicleTypes.find((v) => v._id === t.vehicletypeid)?.name || '-';
              const ownName = t.vehicleownershipid?.name || vehicleOwnerships.find((o) => o._id === t.vehicleownershipid)?.name || '-';

              return (
                <div
                  key={t._id || idx}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm hover:border-blue-400 p-5 flex flex-col justify-between transition-all"
                >
                  <div>
                    <div className="flex items-start justify-between gap-3 pb-3 border-b border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white font-bold text-base flex items-center justify-center shadow-sm">
                          <FaTruck />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-mono font-bold text-blue-700 text-base leading-tight">
                              {t.vehicleno}
                            </h3>
                            <span className="text-xs text-gray-400 font-medium">#{sNo}</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-800 leading-tight mt-0.5">
                            {t.ownername}
                          </p>
                        </div>
                      </div>

                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold border ${
                          isActive
                            ? 'bg-green-50 text-green-700 border-green-200'
                            : 'bg-red-50 text-red-600 border-red-200'
                        }`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></span>
                        {isActive ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3 my-3.5 bg-gray-50/70 p-3 rounded-lg border border-gray-100 text-xs">
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Type / Ownership</span>
                        <span className="font-semibold text-gray-800">{typeName} ({ownName})</span>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Specs</span>
                        <span className="font-semibold text-gray-800">{t.vehiclelength || 0} ft • {t.vehiclecapacity || 0} tons</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-2">
                      <FaPhone className="text-emerald-500" />
                      <span>{t.mobileno || '-'}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 mt-2">
                    <button
                      onClick={() => setViewItem(t)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
                    >
                      <FaEye /> <span>View</span>
                    </button>
                    <button
                      onClick={() => handleOpenModal(t)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition"
                    >
                      <MdEdit /> <span>Edit</span>
                    </button>
                    <button
                      onClick={() => handleDelete(t._id, t.vehicleno)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition"
                    >
                      <MdDelete /> <span>Delete</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Table List View */
          <div className="overflow-x-auto rounded-lg border border-gray-200 mb-6">
            <table className="w-full text-left text-sm text-gray-600 border-collapse">
              <thead className="bg-blue-600 text-white text-xs uppercase font-semibold">
                <tr>
                  <th className="py-3 px-4 text-center w-14">S.No</th>
                  <th className="py-3 px-4">Vehicle No</th>
                  <th className="py-3 px-4">Owner Name</th>
                  <th className="py-3 px-4">Type</th>
                  <th className="py-3 px-4">Ownership</th>
                  <th className="py-3 px-4">Length</th>
                  <th className="py-3 px-4">Capacity</th>
                  <th className="py-3 px-4">Mobile</th>
                  <th className="py-3 px-4 text-center">Status</th>
                  <th className="py-3 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {paginatedData.map((t, idx) => {
                  const isActive = t.status !== 0;
                  const sNo = (currentPage - 1) * pageSize + idx + 1;
                  const typeName = t.vehicletypeid?.name || vehicleTypes.find((v) => v._id === t.vehicletypeid)?.name || '-';
                  const ownName = t.vehicleownershipid?.name || vehicleOwnerships.find((o) => o._id === t.vehicleownershipid)?.name || '-';

                  return (
                    <tr key={t._id || idx} className="hover:bg-blue-50/50 transition">
                      <td className="py-3.5 px-4 text-center text-gray-500 font-semibold">{sNo}</td>
                      <td className="py-3.5 px-4 font-mono font-bold text-blue-600">{t.vehicleno}</td>
                      <td className="py-3.5 px-4 font-bold text-gray-900">{t.ownername}</td>
                      <td className="py-3.5 px-4 font-medium text-gray-700">{typeName}</td>
                      <td className="py-3.5 px-4 font-medium text-gray-700">{ownName}</td>
                      <td className="py-3.5 px-4">{t.vehiclelength || 0} ft</td>
                      <td className="py-3.5 px-4">{t.vehiclecapacity || 0} tons</td>
                      <td className="py-3.5 px-4">{t.mobileno}</td>
                      <td className="py-3.5 px-4 text-center">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${
                            isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                          }`}
                        >
                          {isActive ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        <div className="flex justify-center items-center gap-1.5">
                          <button onClick={() => setViewItem(t)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded">
                            <FaEye />
                          </button>
                          <button onClick={() => handleOpenModal(t)} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded">
                            <MdEdit />
                          </button>
                          <button onClick={() => handleDelete(t._id, t.vehicleno)} className="p-1.5 text-red-600 hover:bg-red-50 rounded">
                            <MdDelete />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="pt-4 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <span>Show</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded px-2 py-1 outline-none font-semibold"
              >
                {[6, 10, 20, 50].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <span>entries per page</span>
              <span className="hidden md:inline ml-3 text-gray-400">
                (Page {currentPage} of {totalPages})
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 rounded-lg border font-semibold text-xs transition disabled:opacity-40 hover:bg-gray-100"
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .map((pageNum, idx, arr) => (
                  <React.Fragment key={pageNum}>
                    {idx > 0 && pageNum - arr[idx - 1] > 1 && <span className="px-1 text-gray-400">...</span>}
                    <button
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 rounded-lg text-xs font-bold transition ${
                        currentPage === pageNum ? 'bg-blue-600 text-white' : 'border text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {pageNum}
                    </button>
                  </React.Fragment>
                ))}

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="px-3 py-1.5 rounded-lg border font-semibold text-xs transition disabled:opacity-40 hover:bg-gray-100"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal: Add/Edit Transport */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden animate-fadeIn">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-700 to-indigo-700 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FaTruck className="text-xl" />
                <h3 className="text-xl font-bold">{editingTransport ? 'Edit Vehicle Record' : 'Add New Vehicle'}</h3>
              </div>
              <button onClick={handleCloseModal} className="text-white/80 hover:text-white p-1 rounded-full">
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                    Vehicle No <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    maxLength={15}
                    placeholder="e.g. TN 37 AB 1234"
                    value={form.vehicleno}
                    onChange={(e) => setForm({ ...form, vehicleno: e.target.value.toUpperCase() })}
                    className={`w-full px-3 py-2 border rounded-lg text-sm font-mono uppercase outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.vehicleno ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.vehicleno && <p className="text-red-500 text-xs mt-1">{formErrors.vehicleno}</p>}
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                    Owner Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. John Doe"
                    value={form.ownername}
                    onChange={(e) => setForm({ ...form, ownername: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.ownername ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.ownername && <p className="text-red-500 text-xs mt-1">{formErrors.ownername}</p>}
                </div>

                {/* Searchable Vehicle Type */}
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                    Vehicle Type <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={vehicleTypeOptions}
                    value={vehicleTypeOptions.find((t) => t.value === form.vehicletypeid) || null}
                    onChange={(opt) => setForm({ ...form, vehicletypeid: opt ? opt.value : '' })}
                    placeholder="Search / select type..."
                    styles={customSelectStyles}
                  />
                  {formErrors.vehicletypeid && <p className="text-red-500 text-xs mt-1">{formErrors.vehicletypeid}</p>}
                </div>

                {/* Searchable Ownership */}
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                    Ownership <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={vehicleOwnershipOptions}
                    value={vehicleOwnershipOptions.find((o) => o.value === form.vehicleownershipid) || null}
                    onChange={(opt) => setForm({ ...form, vehicleownershipid: opt ? opt.value : '' })}
                    placeholder="Search / select ownership..."
                    styles={customSelectStyles}
                  />
                  {formErrors.vehicleownershipid && <p className="text-red-500 text-xs mt-1">{formErrors.vehicleownershipid}</p>}
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Length (ft)</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="0.0"
                    value={form.vehiclelength}
                    onChange={(e) => setForm({ ...form, vehiclelength: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Capacity (tons)</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="0.0"
                    value={form.vehiclecapacity}
                    onChange={(e) => setForm({ ...form, vehiclecapacity: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                    Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    maxLength={10}
                    placeholder="10-digit number"
                    value={form.mobileno}
                    onChange={(e) => setForm({ ...form, mobileno: e.target.value.replace(/\D/g, '') })}
                    className={`w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.mobileno ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.mobileno && <p className="text-red-500 text-xs mt-1">{formErrors.mobileno}</p>}
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={1}>Enabled (Active)</option>
                    <option value={0}>Disabled (Inactive)</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-7 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-bold shadow transition flex items-center gap-2"
                >
                  {submitting && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  <span>{editingTransport ? 'Update Vehicle' : 'Create Vehicle'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: View Details */}
      {viewItem && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-fadeIn">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-700 to-indigo-700 text-white flex items-center justify-between">
              <h3 className="text-lg font-bold">{viewItem.vehicleno}</h3>
              <button onClick={() => setViewItem(null)} className="text-white/80 hover:text-white p-1 rounded-full">
                <FaTimes />
              </button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Owner</span>
                  <strong className="text-gray-800 text-base">{viewItem.ownername}</strong>
                </div>
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Type / Ownership</span>
                  <strong className="text-gray-800">
                    {viewItem.vehicletypeid?.name || '-'} ({viewItem.vehicleownershipid?.name || '-'})
                  </strong>
                </div>
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Length</span>
                  <strong className="text-gray-800">{viewItem.vehiclelength || 0} ft</strong>
                </div>
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Capacity</span>
                  <strong className="text-gray-800">{viewItem.vehiclecapacity || 0} tons</strong>
                </div>
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Mobile</span>
                  <strong className="text-gray-800">{viewItem.mobileno || '-'}</strong>
                </div>
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Status</span>
                  <span className={`inline-block px-2.5 py-0.5 rounded text-xs font-bold ${viewItem.status !== 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {viewItem.status !== 0 ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                </div>
              </div>
            </div>
            <div className="p-4 bg-gray-50 border-t flex justify-end gap-2">
              <button onClick={() => setViewItem(null)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold">
                Close
              </button>
              <button
                onClick={() => {
                  const target = viewItem;
                  setViewItem(null);
                  handleOpenModal(target);
                }}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold shadow"
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transport;